import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogService } from '../../../services';
import './BlogEditor.css';
import { 
    Save, 
    Send, 
    Eye, 
    ArrowLeft,
    Image,
    Bold,
    Italic,
    List,
    Link,
    Heading,
    Quote,
    Code,
    Youtube
} from 'lucide-react';

const BlogEditor = () => {
    const { id: postId } = useParams();
    const navigate = useNavigate();
    const isEditing = !!postId;
    
    const [formData, setFormData] = useState({
        title: '',
        excerpt: '',
        content: '',
        featuredImageUrl: '',
        category: 'News',
        tags: '',
        visibility: 'Public',
        metaDescription: '',
        metaKeywords: ''
    });
    
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingFeatured, setIsUploadingFeatured] = useState(false);
    const [isUploadingContent, setIsUploadingContent] = useState(false);
    const [error, setError] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    const categories = [
        'News',
        'Tips & Techniques',
        'Gear Reviews',
        'Interviews',
        'Competition',
        'Safety',
        'Legislation',
        'Training'
    ];

    const fetchPost = useCallback(async () => {
        if (!postId) return;
        
        setIsLoading(true);
        try {
            const data = await blogService.getPost(postId);
            setFormData({
                title: data.title || '',
                excerpt: data.excerpt || '',
                content: data.content || '',
                featuredImageUrl: data.featuredImageUrl || '',
                category: data.category || 'News',
                tags: data.tags || '',
                visibility: data.visibility || 'Public',
                metaDescription: data.metaDescription || '',
                metaKeywords: data.metaKeywords || ''
            });
        } catch (err) {
            console.error('Error fetching post:', err);
            setError('Failed to load post');
        } finally {
            setIsLoading(false);
        }
    }, [postId]);

    useEffect(() => {
        fetchPost();
    }, [fetchPost]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleContentChange = (e) => {
        setFormData(prev => ({ ...prev, content: e.target.value }));
    };

    const insertFormatting = (tag, closeTag = null) => {
        const textarea = document.getElementById('blog-content');
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = formData.content.substring(start, end);
        const beforeText = formData.content.substring(0, start);
        const afterText = formData.content.substring(end);
        
        let newContent;
        if (closeTag) {
            newContent = `${beforeText}${tag}${selectedText}${closeTag}${afterText}`;
        } else {
            newContent = `${beforeText}${tag}${selectedText}${afterText}`;
        }
        
        setFormData(prev => ({ ...prev, content: newContent }));
    };

    const handleSaveDraft = async () => {
        await savePost(true);
    };

    const handleFeaturedImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsUploadingFeatured(true);
        setError(null);
        try {
            const uploadFormData = new FormData();
            uploadFormData.append('file', file);
            const result = await blogService.uploadImage(uploadFormData);
            if (result?.url) {
                setFormData(prev => ({ ...prev, featuredImageUrl: result.url }));
            }
        } catch (err) {
            console.error('Error uploading featured image:', err);
            setError(err.message || 'Failed to upload featured image');
        } finally {
            setIsUploadingFeatured(false);
            event.target.value = '';
        }
    };

    const handleContentImageUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        setIsUploadingContent(true);
        setError(null);
        try {
            const uploadFormData = new FormData();
            files.forEach(file => uploadFormData.append('files', file));

            const result = await blogService.uploadImages(uploadFormData);
            const imageUrls = (result?.images || []).map(image => image.url).filter(Boolean);

            if (imageUrls.length > 0) {
                const imagesHtml = imageUrls
                    .map(url => `<p><img src="${url}" alt="" /></p>`)
                    .join('\n');
                setFormData(prev => ({
                    ...prev,
                    content: prev.content ? `${prev.content}\n${imagesHtml}` : imagesHtml
                }));
            }
        } catch (err) {
            console.error('Error uploading content images:', err);
            setError(err.message || 'Failed to upload content images');
        } finally {
            setIsUploadingContent(false);
            event.target.value = '';
        }
    };

    const handlePublish = async () => {
        await savePost(false);
    };

    const savePost = async (saveAsDraft) => {
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }
        
        if (!formData.content.trim()) {
            setError('Content is required');
            return;
        }
        
        setIsSaving(true);
        setError(null);
        
        try {
            const body = isEditing 
                ? formData 
                : { ...formData, saveAsDraft };

            const data = isEditing
                ? await blogService.updatePost(postId, body)
                : await blogService.createPost(body);
            
            if (!isEditing) {
                navigate(`/dashboard/blog/editor/${data.postId}`);
            }
            
            alert(saveAsDraft ? 'Draft saved successfully!' : 'Post published successfully!');
            
        } catch (err) {
            console.error('Error saving post:', err);
            setError(err.message || 'Failed to save post');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="blogeditor-loading">
                <div className="blogeditor-loading-spinner"></div>
                <p>Loading post...</p>
            </div>
        );
    }

    return (
        <div className="blogeditor-page">
            <div className="blogeditor-header">
                <button className="blogeditor-back" onClick={() => navigate('/dashboard/admin/blog')}>
                    <ArrowLeft size={18} />
                    Back
                </button>
                
                <h1>{isEditing ? 'Edit Post' : 'Create New Post'}</h1>
                
                <div className="blogeditor-actions">
                    <button 
                        className="blogeditor-btn secondary"
                        onClick={() => setShowPreview(!showPreview)}
                    >
                        <Eye size={16} />
                        {showPreview ? 'Edit' : 'Preview'}
                    </button>
                    
                    <button 
                        className="blogeditor-btn secondary"
                        onClick={handleSaveDraft}
                        disabled={isSaving}
                    >
                        <Save size={16} />
                        Save Draft
                    </button>
                    
                    <button 
                        className="blogeditor-btn primary"
                        onClick={handlePublish}
                        disabled={isSaving}
                    >
                        <Send size={16} />
                        {isSaving ? 'Saving...' : 'Publish'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="blogeditor-error">
                    {error}
                </div>
            )}

            <div className="blogeditor-container">
                <div className="blogeditor-main">
                    {showPreview ? (
                        <div className="blogeditor-preview">
                            <h2>{formData.title || 'Untitled'}</h2>
                            <p className="blogeditor-preview-excerpt">{formData.excerpt}</p>
                            <div 
                                className="blogeditor-preview-content"
                                dangerouslySetInnerHTML={{ __html: formData.content }}
                            />
                        </div>
                    ) : (
                        <>
                            <div className="blogeditor-field">
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="Post title..."
                                    className="blogeditor-title-input"
                                />
                            </div>
                            
                            <div className="blogeditor-field">
                                <textarea
                                    name="excerpt"
                                    value={formData.excerpt}
                                    onChange={handleChange}
                                    placeholder="Write a brief excerpt or summary..."
                                    className="blogeditor-excerpt-input"
                                    rows={2}
                                />
                            </div>
                            
                            <div className="blogeditor-toolbar">
                                <button onClick={() => insertFormatting('<h2>', '</h2>')} title="Heading">
                                    <Heading size={16} />
                                </button>
                                <button onClick={() => insertFormatting('<strong>', '</strong>')} title="Bold">
                                    <Bold size={16} />
                                </button>
                                <button onClick={() => insertFormatting('<em>', '</em>')} title="Italic">
                                    <Italic size={16} />
                                </button>
                                <button onClick={() => insertFormatting('<ul>\n<li>', '</li>\n</ul>')} title="List">
                                    <List size={16} />
                                </button>
                                <button onClick={() => insertFormatting('<blockquote>', '</blockquote>')} title="Quote">
                                    <Quote size={16} />
                                </button>
                                <button onClick={() => insertFormatting('<a href="">', '</a>')} title="Link">
                                    <Link size={16} />
                                </button>
                                <button onClick={() => insertFormatting('<code>', '</code>')} title="Code">
                                    <Code size={16} />
                                </button>
                                <button onClick={() => insertFormatting('<img src="', '" alt="" />')} title="Image">
                                    <Image size={16} />
                                </button>
                                <button onClick={() => insertFormatting('<iframe width="560" height="315" src="https://www.youtube.com/embed/', '" frameborder="0" allowfullscreen></iframe>')} title="YouTube">
                                    <Youtube size={16} />
                                </button>
                            </div>
                            
                            <div className="blogeditor-field">
                                <textarea
                                    id="blog-content"
                                    name="content"
                                    value={formData.content}
                                    onChange={handleContentChange}
                                    placeholder="Write your content here... (HTML supported)"
                                    className="blogeditor-content-input"
                                    rows={20}
                                />
                                <div style={{ marginTop: '8px' }}>
                                    <label className="blogeditor-btn secondary" style={{ cursor: 'pointer' }}>
                                        {isUploadingContent ? 'Uploading...' : 'Upload content images'}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleContentImageUpload}
                                            style={{ display: 'none' }}
                                            disabled={isUploadingContent}
                                        />
                                    </label>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                
                <div className="blogeditor-sidebar">
                    <div className="blogeditor-sidebar-section">
                        <h3>Featured Image</h3>
                        <input
                            type="text"
                            name="featuredImageUrl"
                            value={formData.featuredImageUrl}
                            onChange={handleChange}
                            placeholder="Image URL..."
                        />
                        <div style={{ marginTop: '8px' }}>
                            <label className="blogeditor-btn secondary" style={{ cursor: 'pointer' }}>
                                {isUploadingFeatured ? 'Uploading...' : 'Upload featured image'}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFeaturedImageUpload}
                                    style={{ display: 'none' }}
                                    disabled={isUploadingFeatured}
                                />
                            </label>
                        </div>
                        {formData.featuredImageUrl && (
                            <img 
                                src={formData.featuredImageUrl} 
                                alt="Featured" 
                                className="blogeditor-featured-preview"
                            />
                        )}
                    </div>
                    
                    <div className="blogeditor-sidebar-section">
                        <h3>Category</h3>
                        <select
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                        >
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="blogeditor-sidebar-section">
                        <h3>Tags</h3>
                        <input
                            type="text"
                            name="tags"
                            value={formData.tags}
                            onChange={handleChange}
                            placeholder="tag1, tag2, tag3..."
                        />
                        <small>Separate tags with commas</small>
                    </div>
                    
                    <div className="blogeditor-sidebar-section">
                        <h3>Visibility</h3>
                        <select
                            name="visibility"
                            value={formData.visibility}
                            onChange={handleChange}
                        >
                            <option value="Public">Public (everyone)</option>
                            <option value="MembersOnly">Members Only</option>
                            <option value="Premium">Premium Members</option>
                        </select>
                    </div>
                    
                    <div className="blogeditor-sidebar-section">
                        <h3>SEO</h3>
                        <input
                            type="text"
                            name="metaDescription"
                            value={formData.metaDescription}
                            onChange={handleChange}
                            placeholder="Meta description (160 chars max)"
                            maxLength={160}
                        />
                        <input
                            type="text"
                            name="metaKeywords"
                            value={formData.metaKeywords}
                            onChange={handleChange}
                            placeholder="Meta keywords..."
                            style={{ marginTop: '8px' }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogEditor;
